import DynamicSection from '../dynamic-section/dynamic-section';
import { PURCHASE_TYPE } from '../../src/js/consts';
import ProductReviews from '../product-reviews/product-reviews';
import { ProductSubscriptionManager } from '@/js/product-subscription-utils';

class FilterableSection extends DynamicSection {
  static STORAGE_KEY = Object.freeze('pxl:filter-toggle-state');

  connectedCallback() {
    super.connectedCallback();
    this.initManager();
    this.initSetup();

    // Do not re-render on search pages as we do not support subscriptions on search pages
    if (this.templateName !== 'search') {
      this.reRenderCollectionProductsSection();
    }
  }

  initManager() {
    this.productSubscriptionManager = new ProductSubscriptionManager(this);
  }

  initSetup() {
    this.initVariables();

    if (!this.querySelector(this.filterFormQuery)) return;

    this.setFormListener();
    this.setActiveFilterAnchorListeners();
    this.beforeReplaceMethod = this.beforeReplaceMethod.bind(this);
    this.initEventListeners();
    this.initMobilePopover();
    this.initDesktopDetails();
  }

  /**
   * Sets the sort option based on the last option selected in the sort dropdown by the user
   * @returns {void}
   */
  getSelectedSortOption() {
    // get the sort_by from data-filter-group
    const sortBy = this.querySelector('[data-filter-group-details]');
    // get selected option from sortBy
    const selectedOption = sortBy.querySelector('input:checked');

    if (!selectedOption) return;
    // set the sort_by param to the value of the selected option
    return selectedOption.value;
  }

  initVariables() {
    this.filterFormQuery = '[data-filters-form]';
    this.themeFilters = this.querySelector('[data-theme-filters]');
    this.viewResultsButtons = this.querySelectorAll('[data-view-results]');
    this.filterCTA = this.querySelector('[data-filter-toggle]');
    this.desktopMediaQuery = globalThis.matchMedia('(min-width: 989px)');
    this.oneTimePurchase = this.querySelector('[data-one-time]');
    this.subscriptions = this.querySelector('[data-subscription]');
    this.templateName = this.dataset.templateName;
    this.filterPanel = this.querySelector('[data-filter-panel-options]');
    this.subscriptionAsDefault = this.querySelector(
      '[data-subscription-as-default]'
    );

    this.purchaseType = this.getPurchaseType();

    // By default the filter panel is set to open on desktop but respect the user's preference
    if (
      this.desktopMediaQuery.matches &&
      !sessionStorage.getItem(FilterableSection.STORAGE_KEY)
    ) {
      this.updateFilterToggleState(true);
    }
  }

  initEventListeners() {
    this.desktopMediaQuery.addEventListener(
      'change',
      this.handleResize.bind(this)
    );

    this.initViewResultsButtons();

    this.filterCTA?.addEventListener('click', () => {
      // if on desktop, toggle the open attribute on the theme-filters element
      if (this.desktopMediaQuery.matches) {
        this.themeFilters.hasAttribute('open')
          ? this.themeFilters.removeAttribute('open')
          : this.themeFilters.setAttribute('open', '');
      }

      // if on mobile, add the open attribute to the theme-filters element
      if (!this.desktopMediaQuery.matches) {
        this.themeFilters.setAttribute('open', '');
      }

      // get the current state from session storage
      const newState = !JSON.parse(
        sessionStorage.getItem(FilterableSection.STORAGE_KEY) || 'false'
      );
      this.updateFilterToggleState(newState);
    });
    // Add a class to the filterable-section element when the theme-filters element is toggled
    this.themeFilters.addEventListener('toggle', event => {
      this.classList.toggle('filters-open', event.target.open);
    });

    // One time purchase event listener that renders the main collection products section
    this.oneTimePurchase?.addEventListener('click', event => {
      event.preventDefault();
      this.reRenderSectionName(
        'collection-products-main',
        'filterable-section'
      );
    });

    // Subscriptions event listener that renders the subscription section
    this.subscriptions?.addEventListener('click', event => {
      event.preventDefault();
      this.reRenderSectionName(
        'collection-products-subscription',
        'filterable-section'
      );
    });

    this.querySelector(this.filterFormQuery)?.addEventListener(
      'click',
      event => {
        const resetButton = event.target.closest('a[data-filters-reset]');
        const viewResultsButton = event.target.closest('[data-view-results]');

        if (resetButton) {
          event.preventDefault();
          this.doResetButton();
          this.scrollToFilterPanel();
        }

        if (viewResultsButton) {
          event.preventDefault();
          this.closePopover();
          this.scrollToFilterPanel();
        }
      }
    );
  }

  /**
   * Updates the filter toggle state in localStorage
   * @param {boolean} isActive
   * @returns {void}
   */
  updateFilterToggleState(isActive) {
    sessionStorage.setItem(
      FilterableSection.STORAGE_KEY,
      JSON.stringify(isActive)
    );
  }

  /**
   * Gets the purchase type based on the section ID and the subscription as default metafield
   * @returns {string} PURCHASE_TYPE.SUBSCRIPTION or PURCHASE_TYPE.ONE_TIME
   */
  getPurchaseType() {
    if (this.subscriptionAsDefault) return PURCHASE_TYPE.SUBSCRIPTION;

    const isSubscriptionView =
      this.sectionId === 'collection-products-subscription';

    return isSubscriptionView
      ? PURCHASE_TYPE.SUBSCRIPTION
      : PURCHASE_TYPE.ONE_TIME;
  }

  /**
   * Resets the filters and sorts the products
   * @returns {void}
   */
  doResetButton() {
    let href = this.calculateSectionRenderingUrl();
    const sortBy = this.getSelectedSortOption();
    if (sortBy) {
      href = href + `sort_by=${sortBy}`;
    }

    // If the template name is of type search we also need to add back the search query term
    if (this.templateName === 'search') {
      const searchQuery = document.querySelector('[data-search-query]');
      href = href + `&q=${searchQuery.value}`;
    }

    this.reRenderSection({
      url: href,
      replaceState: true,
      beforeReplaceMethod: this.beforeReplaceMethod
    });

    if (!this.desktopMediaQuery.matches) {
      this.themeFilters.removeAttribute('open');
      this.themeFilters.removeAttribute('popover');
      this.closePopover();
    }
  }

  /**
   * Adds an event listener to the view results buttons
   * @returns {void}
   */
  initViewResultsButtons() {
    for (const button of this.viewResultsButtons) {
      button.addEventListener('click', () => {
        this.closePopover();
      });
    }
  }

  /**
   * Handles the resize event
   * @returns {void}
   */
  handleResize() {
    this.desktopMediaQuery.matches
      ? this.removeMobilePopover() && this.initDesktopDetails()
      : this.initMobilePopover();
  }

  /**
   * Initializes the mobile popover
   * @returns {void}
   */
  initMobilePopover() {
    // Check if we are on a mobile device
    if (!this.desktopMediaQuery.matches) {
      this.themeFilters.setAttribute('popover', '');
    }
  }

  /**
   * Initializes the desktop details
   * @returns {void}
   */
  initDesktopDetails() {
    if (this.desktopMediaQuery.matches) {
      this.themeFilters.setAttribute('open', '');
    }
  }

  /**
   * Removes the popover attribute from the themeFilters element
   * @returns {void}
   */
  removeMobilePopover() {
    this.themeFilters.removeAttribute('popover');
  }

  /**
   * Removes empty parameters from the URLSearchParams object. This is to make the URLs look cleaner.
   * @param {URLSearchParams} parameters
   * @returns {URLSearchParams}
   */
  removeEmptyParams(parameters) {
    const keysForDel = [];
    for (const [key, value] of parameters.entries()) {
      if (value == '') {
        keysForDel.push(key);
      }
    }

    for (const key of keysForDel) {
      parameters.delete(key);
    }

    return parameters;
  }

  /**
   * Calculates the URL to load the section from, based on the filter form
   * @param {Element} filterForm (optional)
   * @returns {String} the URL to load the section from
   */
  calculateSectionRenderingUrl(filterForm) {
    let searchParameters;

    if (filterForm) {
      const formData = new FormData(filterForm);
      searchParameters = this.removeEmptyParams(new URLSearchParams(formData));
    } else {
      searchParameters = new URLSearchParams();
    }

    const rootUrl = globalThis.location.pathname;
    let href = `${rootUrl}?${searchParameters.toString()}`;

    const currentUrl = new URL(globalThis.location.href);
    const currentSearchParameters = currentUrl.searchParams;
    let page = currentUrl.searchParams.get('page');

    currentSearchParameters.delete('page');
    currentSearchParameters.delete('sort_by');
    currentSearchParameters.sort();

    searchParameters.delete('page');
    searchParameters.delete('sort_by');
    searchParameters.sort();

    if (searchParameters.toString() != currentSearchParameters.toString()) {
      page = 1;
    }

    if (page) {
      href += `&page=${page}`;
    }

    return href;
  }

  /**
   * Gets the URL to load the section from, and calls onFilterFormSubmit
   * Used for example when updating the search query term from the search-header
   * @returns {void}
   */
  applyFilters() {
    const filterForm = this.querySelector(this.filterFormQuery);
    const href = this.calculateSectionRenderingUrl(filterForm);
    this.onFilterFormSubmit(href);
  }

  /**
   * Listens for changes in the filters form, gets the calculated URL for section rendering, hands off to onFilterFormSubmit
   * @returns {void}
   */
  setFormListener() {
    const filterForm = this.querySelector(this.filterFormQuery);

    filterForm.addEventListener('change', () => {
      const href = this.calculateSectionRenderingUrl(filterForm);
      this.onFilterFormSubmit(href);
    });
  }

  /**
   * Sets the event listeners for the buttons/links in the active filters section
   * @returns {void}
   */
  setActiveFilterAnchorListeners() {
    const activeFiltersDiv = this.querySelector('[data-active-filters]');

    // Custom section render links e.g. remove single filter buttons, remove all filters, etc.
    // very handily, shopify provides us with a URL that will remove a single filter, so we can just pass that to section rendering.
    activeFiltersDiv.addEventListener('click', event => {
      const anchor = event.target.closest('a'); // the event.target might be a span, or an icon within the anchor.
      if (!anchor) return;

      const { dataset, href } = anchor;

      if (Object.hasOwn(dataset, 'filterAnchor')) {
        event.preventDefault();
        this.reRenderSection({
          url: href,
          replaceState: true,
          beforeReplaceMethod: this.beforeReplaceMethod
        });
      }

      // Reset all filters button(s). Note, there's no handy URL provided here, so we have to calculate it.
      else if (Object.hasOwn(dataset, 'filtersReset')) {
        event.preventDefault();
        const resetHref = this.calculateSectionRenderingUrl();
        this.onFilterFormSubmit(resetHref);
      }
    });
  }

  /**
   * Calls section rendering with the URL provided
   * @param {String} href - the URL to load the section from
   * @param {Object} options
   */
  async onFilterFormSubmit(href) {
    await this.reRenderSection({
      url: href,
      replaceState: true,
      beforeReplaceMethod: this.beforeReplaceMethod
    });

    this.updateSearchHeader(href);
  }

  /**
   * Finds the details elements with or without [open] and then applies this attribute to the matching elements in newSectionElement
   * @param {HTMLElement} newSectionElement
   * @returns {HTMLElement}
   * @note This prevents the state of any closed details elements being lost when the section is re-rendered
   */
  beforeReplaceMethod(newSectionElement) {
    // Find the details element in the new section element and handle both desktop and mobile views
    const details = newSectionElement.querySelector('[data-theme-filters]');
    if (!details) return;

    const isDesktopView = this.desktopMediaQuery.matches;

    // Check state of the filter panel based on view mode and session storage
    const shouldBeOpen = isDesktopView
      ? this.themeFilters.hasAttribute('open') &&
        sessionStorage.getItem(FilterableSection.STORAGE_KEY) === 'true'
      : this.themeFilters.matches('[popover][open]');

    if (shouldBeOpen) {
      // Apply appropriate attributes based on view mode
      details.setAttribute('open', '');

      if (!isDesktopView) {
        details.setAttribute('popover', '');
      }

      // If we are on desktop and go onto a new page or toggle via the panel tabs, we need to open the filter panel
      this.initDesktopDetails();
    }

    const allCurrentDetails = this.querySelectorAll(
      '[data-filter-group-details]'
    );

    // For each current detail, set matching state in new section
    for (const currentDetail of allCurrentDetails) {
      const matchingDetail = newSectionElement.querySelector(
        `[data-filter-group-details="${currentDetail.dataset.filterGroupDetails}"]`
      );

      if (matchingDetail) {
        if (currentDetail.hasAttribute('open')) {
          matchingDetail.setAttribute('open', '');
        } else {
          matchingDetail.removeAttribute('open');
        }
      }
    }

    return newSectionElement;
  }

  /**
   * Reloads the search header section.
   * @returns {void}
   */
  async updateSearchHeader(href) {
    const searchHeader = this.querySelector('search-header');
    if (!searchHeader) return;
    searchHeader.reRenderSection({ url: href });
  }

  /**
   * Closes the popover if it's open
   * @returns {void}
   */
  closePopover() {
    const popover = this.themeFilters.closest('[popover][open]');
    if (popover) {
      popover.hidePopover();
      // Remove open attribute from themeFilters
      this.themeFilters.removeAttribute('open');
      // Scroll to filter panel section
      const filterPanelSection = document.querySelector(
        '#filter-panel-section'
      );
      filterPanelSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  /**
   * Rerenders the section with the given name
   * @param {String} sectionName - The name of the section to rerender
   * @param {String} sectionNameToReplace - The name of the section to replace
   * @returns {void}
   */
  reRenderSectionName(sectionName, sectionNameToReplace) {
    const filterForm = this.querySelector(this.filterFormQuery);
    const href = this.calculateSectionRenderingUrl(filterForm);

    this.reRenderSection({
      url: href,
      replaceState: true,
      beforeReplaceMethod: this.beforeReplaceMethod,
      sectionName: sectionName,
      sectionNameToReplace: sectionNameToReplace
    });
  }

  /**
   * Rerenders the collection products section
   * @returns {void}
   */
  reRenderCollectionProductsSection() {
    if (this.purchaseType === PURCHASE_TYPE.SUBSCRIPTION) {
      this.reRenderSectionName(
        'collection-products-subscription',
        'filterable-section'
      );
    } else {
      this.reRenderSectionName(
        'collection-products-main',
        'filterable-section'
      );
    }
  }
  /**
   * Initializes the subscription manager
   * @returns {void}
   */
  async initSubscriptionManager() {
    await customElements.whenDefined('quantity-input');
    this.productSubscriptionManager.init();
  }

  /**
   * Sets the state of the filter panel on mobile
   * @returns {void}
   * @note This is needed because after we section render we lose the state of the filter panel.
   */
  setMobileFilterPanelState() {
    if (!this.desktopMediaQuery.matches) {
      this.initMobilePopover();

      if (
        this.themeFilters.hasAttribute('open') &&
        this.themeFilters.hasAttribute('popover')
      ) {
        this.themeFilters.showPopover();
      } else {
        this.closePopover();
      }
    }
  }

  /**
   * Scrolls to the filter panel section
   * @returns {void}
   */
  scrollToFilterPanel() {
    const filterPanelSection = document.querySelector('#filter-panel-section');
    filterPanelSection.scrollIntoView({ behavior: 'smooth' });
  }

  afterSectionRender(options) {
    // First run the parent method defined in DynamicSection
    super.afterSectionRender(options);

    this.setMobileFilterPanelState();
    ProductReviews.loadReviewsForProduct();

    this.purchaseType = this.getPurchaseType();

    if (this.purchaseType === PURCHASE_TYPE.SUBSCRIPTION) {
      this.initSubscriptionManager();
    }
  }
}

customElements.get('filterable-section') ||
  customElements.define('filterable-section', FilterableSection);

//  also exporting this so it can be extended in other elements too.
export default FilterableSection;
